import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLinks } from "../lib/LinksContext";
import { useContent } from "../lib/ContentContext";
import { SOCIAL_LINKS } from "../lib/socials";

export default function Footer() {
  const links = useLinks();
  const c = useContent();
  const socials = SOCIAL_LINKS.filter((s) => links[s.key]);

  const NAV = [
    { to: "/eugenie", slug: "eugenie", label: c("nav.eugenie") },
    { to: "/clinique", slug: "clinique", label: c("nav.clinique") },
    { to: "/conversations", slug: "conversations", label: c("nav.conversations") },
    { to: "/medias", slug: "medias", label: c("nav.medias") },
    { to: "/conferences", slug: "conferences", label: c("nav.conferences") },
    { to: "/projets", slug: "projets", label: c("nav.projets") },
    { to: "/contact", slug: "contact", label: c("nav.contact") },
  ];

  const QUICK = [
    ...(links.sexualis ? [{ href: links.sexualis, label: "Sexualis", id: "footer-quick-sexualis" }] : []),
    ...(links.fesses ? [{ href: links.fesses, label: "Fesses & Tendresse", id: "footer-quick-fesses" }] : []),
    { to: "/medias", label: c("footer.quick.medias"), id: "footer-quick-medias" },
    { to: "/ressources", label: c("footer.quick.ressources"), id: "footer-quick-ressources" },
    { to: "/confidentialite", label: c("footer.quick.confidentialite"), id: "footer-quick-confidentialite" },
    { to: "/confidentialite#mentions-legales", label: c("footer.quick.mentions"), id: "footer-quick-mentions" },
  ];

  return (
    <footer data-testid="site-footer" className="bg-sage text-charcoal">
      <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-28">
        <div className="grid gap-14 md:grid-cols-12">
          <div className="md:col-span-4">
            <img src="/logo-full.png" alt="Eugénie Larrivée · Sexologue et Psychothérapeute" className="h-16 w-auto" />
            <p className="mt-3 text-sm leading-relaxed text-smoke">
              {c("footer.sous-titre")}
            </p>
            {socials.length > 0 && (
              <div className="mt-6 flex items-center gap-4">
                {socials.map(({ key, label, Icon }) => (
                  <a
                    key={key}
                    href={links[key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`footer-social-${key}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-charcoal/20 text-smoke transition-colors duration-300 hover:border-clay hover:text-clay"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="md:col-span-2">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-smoke/70">{c("footer.nav.titre")}</p>
            <ul className="mt-5 space-y-2.5">
              {NAV.map((n) => (
                <li key={n.to}>
                  <Link
                    to={n.to}
                    data-testid={`footer-link-${n.slug}`}
                    className="text-sm text-smoke transition-colors duration-300 hover:text-clay"
                  >
                    {n.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-smoke/70">{c("footer.quick.titre")}</p>
            <ul className="mt-5 space-y-2.5">
              {QUICK.map((q) => (
                <li key={q.id}>
                  {q.to ? (
                    <Link to={q.to} data-testid={q.id} className="text-sm text-smoke transition-colors duration-300 hover:text-clay">
                      {q.label}
                    </Link>
                  ) : (
                    <a href={q.href} {...(q.href !== "#" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      data-testid={q.id} className="group inline-flex items-center gap-1.5 text-sm text-smoke transition-colors duration-300 hover:text-clay">
                      {q.label}
                      <ArrowUpRight className="h-3 w-3 opacity-0 transition-all duration-300 group-hover:opacity-100" />
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs font-medium uppercase tracking-[0.25em] text-smoke/70">{c("footer.contact.titre")}</p>
            <p className="mt-5 text-sm leading-relaxed text-smoke">
              {c("footer.contact.texte")}
            </p>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="group mt-5 inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal"
            >
              {c("footer.contact.cta")}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
        </div>

        <div className="mt-16 border-t border-charcoal/10 pt-10 text-center">
          <p className="font-hand text-2xl text-clay md:text-3xl">
            {c("footer.signature")}
          </p>
          <p data-testid="footer-copyright" className="mt-6 text-xs text-smoke/70">
            © {new Date().getFullYear()} Eugénie Larrivée. Tous droits réservés.
          </p>
        </div>
      </div>
    </footer>
  );
}
